import * as React from 'react';
import { t } from 'i18next';

import { PopupMenu, SaveType, SidebarModule, MessageBoxType } from '../constants/Enums';
import { VisibilityActions } from './VisibilityActions';
import { FloorPlanActions } from './FloorPlanActions';
import { UIHelper, PromiseCallbacks } from '../utils/UIHelper';
import { ProjectAttributes, ProjectManagerActions } from './ProjectManagerActions';
import { SessionActions } from './SessionActions';
import { ViewSettingsActions } from './ViewSettingsActions';
import { GlobalHelper } from '../utils/GlobalHelper';
import { SceneActions } from './SceneActions';
import { SceneStore } from '../stores/SceneStore';
import { BaseDialogProps, BaseDialog } from '../components/dialog/BaseDialog';
import { ClickButton } from '../components/ClickButton';
import { NewProjectDialog } from '../components/dialog/NewProjectDialog';
import { ImportDialogProps, ImportDialog } from '../components/dialog/ImportDialog';

import core = egr.wcf.core;
import utils = egr.wcf.utils;

interface OBKImportArgument {
    type: 'OBK';
    content: Blob;
}

interface PECImportAgument {
    type: 'PEC';
    content: Blob | ArrayBuffer;
}

interface PBOXImportAgrument {
    type: 'PBOX';
    content: Blob | ArrayBuffer | core.io.DocumentContainer;
}

export type ImportArgument = OBKImportArgument | PECImportAgument | PBOXImportAgrument;

export class NewSceneCancelError extends utils.CustomError {
    constructor(message?: string) {
        super(message);
        Object.setPrototypeOf(this, NewSceneCancelError.prototype);
    }
}

export class UnsupportedFileError extends utils.CustomError {
    constructor(message?: string) {
        super(message);
        Object.setPrototypeOf(this, UnsupportedFileError.prototype);
    }
}

export abstract class DialogActions {
    public static fileToImportArgument(file: File): ImportArgument {
        const filename: string = file.name.toLocaleLowerCase();
        if (filename.endsWith('.pec')) {
            return {
                type: 'PEC',
                content: file
            };
        }
        if (filename.endsWith('.pbox')) {
            return {
                type: 'PBOX',
                content: file
            };
        }
        if (filename.endsWith('.obk')) {
            return {
                type: 'OBK',
                content: file
            };
        }

        throw new UnsupportedFileError('Unsupported file');
    }

    public static triggerFloorPlanUpload(): void {
        VisibilityActions.hidePopupMenu(PopupMenu.GROUND_SETUP);
        FloorPlanActions.getImageFile();
    }

    public static async openProjectFromFileInput(fileInput: HTMLInputElement): Promise<void> {
        if (fileInput.files == null || fileInput.files.length === 0 || !fileInput.value) {
            return;
        }

        try {
            return DialogActions.openProjectFromFile(
                DialogActions.fileToImportArgument(fileInput.files[0])
            );
        } catch (error) {
            if (error instanceof UnsupportedFileError) {
                UIHelper.showMessage(t('This file can not be opened. You can open OBK, PBOX and PEC files only.'));
            } else {
                throw error;
            }
        } finally {
            // Ensure that a `change` event is triggert even if the user
            // select the same file twice
            fileInput.value = '';
        }
    }

    public static async openProjectFromFile(file: ImportArgument): Promise<void> {
        let waitPromiseCallbacks: PromiseCallbacks<void> | undefined;
        try {
            const saveType: SaveType = await DialogActions.newProject();

            let attributes: false | ProjectAttributes | undefined;
            let callback: (() => Promise<void>) | undefined;

            switch (file.type) {
                case 'OBK':
                    callback = (): Promise<void> => {
                        return SessionActions.loadObkFile(file.content, false);
                    };
                    break;
                case 'PEC':
                    callback = async (): Promise<void> => {
                        await SessionActions.loadPecFile(file.content);
                    };
                    break;
                case 'PBOX':
                    // Note: the .pbox container contains information about the pCon.login id
                    // -> the loading process must be paused after the pCon.login data were loaded
                    // -> then the pCon.login data from the .pbox file must be validated based on the current active user
                    // -> finally the `newScene` action can be called if (and only if) the user is allowed to load the container
                    waitPromiseCallbacks = UIHelper.getPromiseCallbacks<void>();
                    const attributesPromiseCallbacks: PromiseCallbacks<false | ProjectAttributes | undefined> =
                        UIHelper.getPromiseCallbacks<false | ProjectAttributes | undefined>();
                    const loadPromise: Promise<void> = ViewSettingsActions.withLock<void>(async (): Promise<void> => {
                        const options: core.AppDocumentLoadOptions = {
                            onValidateDocument: (docHeader: core.io.DocumentHeader, appData: utils.GroupedMap): Promise<void> => {
                                setTimeout(
                                    (): void => {
                                        // Note: ugly hack (the `false` value) because of changed requirements on the day before the release
                                        attributesPromiseCallbacks.resolve(appData.getData<false | ProjectAttributes>('pCon.login', 'userinfo'));
                                    },
                                    0
                                );
                                if (waitPromiseCallbacks != null) {
                                    return waitPromiseCallbacks.promise;
                                }
                                return Promise.resolve();
                            }
                        };

                        try {
                            if (file.content instanceof Blob || file.content instanceof ArrayBuffer) {
                                await GlobalHelper.coreApp.document.loadFromZip(file.content, options);
                            } else {
                                await GlobalHelper.coreApp.document.loadFromContainer(file.content, options);
                            }
                        } catch (error) {
                            if (waitPromiseCallbacks != null) {
                                waitPromiseCallbacks = undefined;
                                throw error;
                            }
                        }
                    });

                    // wait until the pCon.login data from the .pbox file is avalibal
                    attributes = await attributesPromiseCallbacks.promise;

                    callback = (): Promise<void> => {
                        if (waitPromiseCallbacks != null) {
                            setTimeout(waitPromiseCallbacks.resolve, 0);
                        }
                        return loadPromise;
                    };
                    break;
                default:
                    break;
            }

            if (callback == null) {
                throw new UnsupportedFileError('Unsupported file');
            }

            await SceneActions.newScene(
                {
                    projectAttributes: attributes !== false ? attributes : undefined,
                    projectLoadingHook: callback,
                    validateProjectAttibutesIfNull: attributes != null,
                    validateProjectAttibutesWithoutProjectManager: true
                },
                saveType
            );
            ViewSettingsActions.changeHamburger(null);
            ViewSettingsActions.changeSidebar(SidebarModule.NONE);
        } catch (error) {
            if (!(error instanceof NewSceneCancelError)) {
                if (waitPromiseCallbacks != null) {
                    setTimeout(waitPromiseCallbacks.reject, 0);
                    waitPromiseCallbacks = undefined;
                }

                setTimeout(
                    (): void => {
                        SceneActions.newScene(null, SaveType.NOT);
                    },
                    0
                );

                UIHelper.handleProjectLoadingError(error);
            }
        }
    }

    public static duplicateProject(projectId: string, defaultName: string | null): void {
        VisibilityActions.setDialog({
            closeCallback: () => {
                ProjectManagerActions.toggleProjectSelection(null, true);
            },
            callback: async (value: string | null): Promise<void> => {
                await ProjectManagerActions.duplicateProject(projectId, value == null ? '' : value);
                await ProjectManagerActions.updateProjectList();
                ProjectManagerActions.toggleProjectSelection(null, true);
                ProjectManagerActions.disableEditMode();
            },
            label: t('Please enter a name for the new project.'),
            default: defaultName,
            type: 'text',
            noTextHelper: true
        });
    }

    public static setProjectName(projectId: string, defaultName?: string): void {
        VisibilityActions.setDialog({
            closeCallback: () => {
                ProjectManagerActions.toggleProjectSelection(null, true);
            },
            callback: async (value: string): Promise<void> => {
                await ProjectManagerActions.setProjectName(projectId, value);
                ProjectManagerActions.toggleProjectSelection(null, true);
                ProjectManagerActions.disableEditMode();
            },
            label: t('Please enter a name for the project.'),
            default: defaultName ? defaultName : t('Unnamed'),
            type: 'text',
            noTextHelper: true
        });
    }

    public static showDownloadLink(link: string, filename: string): void {
        ViewSettingsActions.showMessage({
            wrap: true,
            element: React.createElement('div', {className: 'dialog-page-resizer'}, [
                React.createElement('div', {className: 'dialog-page-header', style: {textAlign: 'center'}}, [filename]),
                React.createElement(
                    'a',
                    {
                        href: link, target: '_blank',
                        className: 'submit-button full-button', download: filename
                    },
                    [t('Download')]
                )
            ]),
            type: MessageBoxType.DEFAULT
        });
    }

    /**
     * Shows dialog with 'Ok' and 'Cancel' Buttons, the promise resolves with 'true' when Ok-Button is clicked,
     * and resolves with 'false' on Cancel-Button click or click outside of the dialog
     * @param label
     */
    public static getPromiseDialog(label: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            ViewSettingsActions.showMessage({
                wrap: false,
                element: React.createElement<BaseDialogProps>(
                    BaseDialog,
                    {
                        label: label,
                        closeCallback: () => { resolve(false); },
                        buttons: [
                            {label: t('Ok'), callback: () => { resolve(true); }},
                            {label: t('Cancel'), callback: () => { resolve(false); }}
                        ]
                    },
                    []
                ),
                type: MessageBoxType.DEFAULT
            });
        });
    }

    public static showErrorMessage(label: string, retry: () => void, cancel: () => void): void {
        ViewSettingsActions.showMessage({
            wrap: true,
            element: React.createElement('div', {className: 'dialog-page-resizer'}, [
                React.createElement('div', {className: 'dialog-page-header'}, [label]),
                React.createElement(
                    ClickButton,
                    {className: 'cancel-button', callback: (): void => { ViewSettingsActions.showMessage(null); cancel(); }},
                    [t('Cancel')]
                ),
                React.createElement(
                    ClickButton,
                    {className: 'submit-button', callback: (): void => { ViewSettingsActions.showMessage(null); retry(); }},
                    [t('Retry')]
                ),
            ]),
            type: MessageBoxType.DEFAULT
        });
    }

    public static newProject(skip: boolean = false): Promise<SaveType> {
        if (skip) {
            return Promise.resolve(SaveType.NOT);
        }

        if (SceneStore.getState().unsavedChanges) {
            return new Promise<SaveType>((resolve, reject) => {
                ViewSettingsActions.showMessage({
                    wrap: false,
                    element: React.createElement(NewProjectDialog, {
                        saveCallback: () => { resolve(SaveType.DEVICE); },
                        noSaveCallback: () => { resolve(SaveType.NOT); },
                        cancelCallback: () => { reject(new NewSceneCancelError()); }
                    }),
                    type: MessageBoxType.DEFAULT
                });
            });
        }

        return Promise.resolve(SaveType.NOT);
    }

    public static showProjectNameDialog(defaultName?: string): Promise<string> {
        return new Promise<string>((resolve, reject) => {
            VisibilityActions.setDialog({
                callback: resolve,
                closeCallback: reject,
                label: t('Please enter a name for the project.'),
                default: defaultName ? defaultName : t('Unnamed'),
                type: 'text',
                noTextHelper: true
            });
        });
    }

    public static showImportDialog(importDialogProps: ImportDialogProps): void {
        ViewSettingsActions.showMessage({
            wrap: true,
            element: React.createElement(ImportDialog, importDialogProps),
            type: MessageBoxType.DEFAULT
        });
    }
}


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/actions/DialogActions.ts